import { BellIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, useCallback, useRef } from 'react';
import type { UIEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { notificationService } from '../../../services/NotificationService';
import {
  fetchNotificationsStart,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  markAllNotificationsAsReadStart,
  markAllNotificationsAsReadSuccess,
  markAllNotificationsAsReadFailure
} from '../../../store/slices/notificationSlice';
import { SideDrawer } from '../../ui/SideDrawer/SideDrawer';
import Notification from './components/Notification';
import { NotificationActions } from './components/NotificationActions';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import { NotificationBellIcon } from '../../../icons/SvgIcons';

export function Notifications() {
  const dispatch = useAppDispatch();
  const { notifications, unreadCount, loading } = useAppSelector(
    (state) => state.notification
  );
  const [openSideDrawer, setOpenSideDrawer] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const notificationsRef = useRef(notifications);
  const DEFAULT_LIMIT = 10;

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const fetchNotifications = useCallback(
    async ({
      page = 1,
      append = false
    }: {
      page?: number;
      append?: boolean;
    } = {}) => {
      try {
        dispatch(fetchNotificationsStart());
        const response = await notificationService.getNotifications({
          page,
          limit: DEFAULT_LIMIT
        });

        const mergedNotifications = append
          ? [
              ...notificationsRef.current,
              ...response.items.filter(
                (nextItem) =>
                  !notificationsRef.current.some(
                    (existingItem) => existingItem.id === nextItem.id
                  )
              )
            ]
          : response.items;

        dispatch(
          fetchNotificationsSuccess({
            notifications: mergedNotifications,
            unreadCount: response.unreadCount
          })
        );
        setCurrentPage(page);
        setHasMoreNotifications(mergedNotifications.length < response.total);
      } catch (error: any) {
        dispatch(
          fetchNotificationsFailure(
            error.message || 'Failed to fetch notifications'
          )
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchNotifications({ page: 1, append: false });
  }, [fetchNotifications]);

  useEffect(() => {
    if (openSideDrawer) {
      return;
    }

    const interval = setInterval(() => {
      fetchNotifications({ page: 1, append: false });
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications, openSideDrawer]);

  const handleDrawerScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (loading || isLoadingMore || !hasMoreNotifications) {
        return;
      }

      const target = event.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 24;

      if (!nearBottom) {
        return;
      }

      setIsLoadingMore(true);
      void fetchNotifications({ page: currentPage + 1, append: true }).finally(
        () => setIsLoadingMore(false)
      );
    },
    [
      loading,
      isLoadingMore,
      hasMoreNotifications,
      fetchNotifications,
      currentPage
    ]
  );

  const handleMarkAllAsRead = async () => {
    try {
      dispatch(markAllNotificationsAsReadStart());
      await notificationService.markAllNotificationsAsRead();
      dispatch(markAllNotificationsAsReadSuccess());
    } catch (error: any) {
      dispatch(
        markAllNotificationsAsReadFailure(
          error.message || 'Failed to mark all notifications as read'
        )
      );
    }
  };

  const filteredNotifications = showUnreadOnly
    ? notifications.filter((notification) => notification.isUnread)
    : notifications;

  const unreadNotificationsCount = unreadCount;
  const hasNotifications = filteredNotifications.length > 0;

  const drawerItems = hasNotifications
    ? filteredNotifications.map((notification) => (
        <Notification
          key={notification.id}
          id={notification.id}
          name={notification.actorName || 'System'}
          title={notification.title}
          description={notification.content}
          date={notification.date}
          relatedAISystem={notification.relatedAiSystemName || 'System'}
          relatedAiSystemId={notification.relatedAiSystemId}
          type={notification.type}
          actorName={notification.actorName}
          isUnread={notification.isUnread}
          onRedirectAction={() => setOpenSideDrawer(false)}
        />
      ))
    : [
        <EmptyState
          key="empty-notifications"
          icon={NotificationBellIcon()}
          title={
            showUnreadOnly ? 'No unread notifications' : 'No notifications yet'
          }
          description={
            showUnreadOnly
              ? "You're all caught up."
              : 'You will see updates here as activity happens.'
          }
          classes="!p-12 border-0 !shadow-none"
        />
      ];

  const notificationsWithLoading = isLoadingMore
    ? [
        ...drawerItems,
        <div
          key="notifications-loading-more"
          className="py-3 text-sm text-gray-550 text-center"
        >
          Loading more...
        </div>
      ]
    : drawerItems;

  return (
    <>
      <button
        className="w-8 h-8 hover:bg-gray-100 rounded-full transition-colors duration-200 relative flex items-center justify-center"
        onClick={() => setOpenSideDrawer(true)}
        id="notifications-open-button"
        data-testid="notifications-open-button"
      >
        <BellIcon className="text-gray-400 hover:text-gray-600 transition-colors duration-200 h-6 w-6" />
        {unreadNotificationsCount > 0 && (
          <span className="text-[8px] text-white bg-orange-600 rounded-full min-w-3 min-h-3 px-[1px] flex items-center justify-center absolute top-0 right-0">
            {unreadNotificationsCount}
          </span>
        )}
      </button>

      {openSideDrawer && (
        <SideDrawer
          isOpen={openSideDrawer}
          onClose={() => setOpenSideDrawer(false)}
          title="Notifications"
          listItems={notificationsWithLoading}
          showActionButton={true}
          listItemsClasses={`${hasNotifications ? '' : 'h-full'}`}
          onScrollContent={handleDrawerScroll}
          actionButton={
            <NotificationActions
              onMarkAllAsRead={handleMarkAllAsRead}
              showUnreadOnly={showUnreadOnly}
              setShowUnreadOnly={setShowUnreadOnly}
              disableMarkAllAsRead={unreadNotificationsCount === 0}
            />
          }
        />
      )}
    </>
  );
}
